import {
    mdiArrowBottomLeft,
    mdiArrowTopRight,
    mdiAt,
    mdiBadgeAccountHorizontalOutline,
    mdiBankOutline,
    mdiEmailOutline,
    mdiFax,
    mdiFolderOpenOutline,
    mdiHandshakeOutline,
    mdiPhoneOutline,
    mdiReceiptTextOutline,
    mdiRobotOutline,
    mdiSwapHorizontal,
} from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import { fileCabinetDocumentTypeLabel } from '../../services/file-cabinet/file-cabinet.document-types';
import type { FileCabinetChannel, FileCabinetDirection } from '../../services/file-cabinet/file-cabinet.types';
import { FILE_CABINET_CHANNELS, FILE_CABINET_QUICK_TYPES } from '../../services/file-cabinet/file-cabinet.views';
import { FileCabinetCount, FileCabinetStore } from '../../stores/file-cabinet/file-cabinet.store';
import { TypeCountBadge, TypeFilterBar, TypeFilterChip } from '../base/type-filter-bar/type-filter-bar';
import { methodLabel } from '../../services/file-cabinet/file-cabinet.records';
import * as S from './file-cabinet.styles';

const CHANNEL_ICONS: Record<FileCabinetChannel, string> = {
    docs: mdiFolderOpenOutline,
    mail: mdiEmailOutline,
    fax: mdiFax,
    email: mdiAt,
    call: mdiPhoneOutline,
};

const DIRECTION_ICONS: Record<FileCabinetDirection, string> = {
    in: mdiArrowBottomLeft,
    out: mdiArrowTopRight,
    other: mdiSwapHorizontal,
    ai: mdiRobotOutline,
};

const TYPE_ICONS: Record<string, string> = {
    FINANCIAL_REPORTS: mdiBankOutline,
    PROVIDER_CONTRACT: mdiHandshakeOutline,
    B2B_INVOICE: mdiReceiptTextOutline,
    BUSINESS_W9: mdiBadgeAccountHorizontalOutline,
};

/**
 * A count as the toggle and shortcuts show it. Never `0` for something we do not know (NS-3):
 * loading or failed is `—`, and a channel this user cannot read says so.
 */
export function countText(count: FileCabinetCount): string {
    if (count === 'no-access') {
        return 'No access';
    }
    return count === undefined ? '—' : count.toLocaleString();
}

/** The channel tabs, the direction toggle under them, and on All Documents the type shortcuts. */
export const FileCabinetNav = observer(({ store }: { store: FileCabinetStore }): React.ReactElement => {
    const channel = FILE_CABINET_CHANNELS.find(c => c.channel === store.channel);

    return (
        <>
            <S.Tabs<FileCabinetChannel>
                tabs={FILE_CABINET_CHANNELS.map(c => ({
                    id: c.channel,
                    label: (
                        <S.TabLabel>
                            <Icon path={CHANNEL_ICONS[c.channel]} size={0.75} />
                            {c.label}
                        </S.TabLabel>
                    ),
                }))}
                activeTab={store.channel}
                onTabChange={store.setChannel}
            />

            {channel && channel.directions.length > 0 && (
                <S.ToggleRow>
                    <TypeFilterBar>
                        {channel.directions.map(d => {
                            const active = !!d.view && store.view === d.view;
                            const chip = (
                                <TypeFilterChip
                                    key={d.direction}
                                    active={active}
                                    disabled={!d.view}
                                    onClick={(): void => store.setDirection(d.direction)}
                                >
                                    <Icon path={DIRECTION_ICONS[d.direction]} size={0.6} />
                                    {d.label}
                                    {d.sub && <S.ToggleSub>· {d.sub}</S.ToggleSub>}
                                    {d.view && (
                                        <TypeCountBadge active={active}>{countText(store.viewCounts[d.view])}</TypeCountBadge>
                                    )}
                                </TypeFilterChip>
                            );
                            // A disabled button shows no title of its own; its reason goes on a wrapper (and,
                            // for keyboard and screen-reader users who never hover, in `sub` on the chip).
                            const title = d.disabled ?? d.methods?.map(m => methodLabel(m)).join(', ');
                            return title ? (
                                <S.ToggleSlot key={d.direction} title={title}>
                                    {chip}
                                </S.ToggleSlot>
                            ) : (
                                chip
                            );
                        })}
                    </TypeFilterBar>
                </S.ToggleRow>
            )}

            {store.channel === 'docs' && (
                <S.ToggleRow>
                    <TypeFilterBar>
                        <TypeFilterChip
                            active={store.documentType === undefined}
                            onClick={(): void => store.setDocumentType(undefined)}
                        >
                            All types
                            <TypeCountBadge active={store.documentType === undefined}>
                                {countText(store.typeCounts.all)}
                            </TypeCountBadge>
                        </TypeFilterChip>
                        {FILE_CABINET_QUICK_TYPES.map(type => {
                            const active = store.documentType === type;
                            return (
                                <TypeFilterChip
                                    key={type}
                                    active={active}
                                    onClick={(): void => store.setDocumentType(active ? undefined : type)}
                                >
                                    <Icon path={TYPE_ICONS[type]} size={0.6} />
                                    {fileCabinetDocumentTypeLabel(type)}
                                    <TypeCountBadge active={active}>{countText(store.typeCounts[type])}</TypeCountBadge>
                                </TypeFilterChip>
                            );
                        })}
                    </TypeFilterBar>
                </S.ToggleRow>
            )}
        </>
    );
});
